import { useEffect, useState, useCallback } from "react";
import { api, apiError, getToken, getTenant } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { toast } from "sonner";
import { FileText, Plus, Download, Trash2, ExternalLink } from "lucide-react";
import FileDocumentDialog from "@/components/documents/FileDocumentDialog";

export const STATUS_META = {
  draft: { label: "Draft", cls: "border-slate-300 text-slate-600 bg-slate-50" },
  awaiting_signature: { label: "Awaiting signature", cls: "border-amber-200 text-amber-700 bg-amber-50" },
  active: { label: "Active", cls: "border-teal-200 text-teal-700 bg-teal-50" },
  expired: { label: "Expired", cls: "border-red-200 text-red-700 bg-red-50" },
};

export const StatusChip = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.draft;
  return <span className={`chip ${m.cls}`} data-testid="doc-status-chip">{m.label}</span>;
};

export const expiringSoon = (d) => {
  if (!d.expiry_date || d.status === "expired") return false;
  const days = (new Date(d.expiry_date) - new Date()) / 86400000;
  return days >= 0 && days <= 30;
};

export const downloadDoc = (d) => {
  if (d.kind === "link") { window.open(d.url, "_blank", "noopener"); return; }
  const base = process.env.REACT_APP_BACKEND_URL;
  window.open(`${base}/api/files/${d.id}/download?auth=${getToken()}&tenant=${getTenant()}`, "_blank", "noopener");
};

export default function DocumentsPanel({ organizationId, opportunityId, orgName }) {
  const { has, user } = useAtlas();
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const canManage = has("files.manage");
  const canDelete = (d) => canManage && (d.uploaded_by === user?.id || has("admin.access"));

  const qs = opportunityId ? `opportunity_id=${opportunityId}` : `organization_id=${organizationId}`;
  const load = useCallback(() => { api.get(`/documents?${qs}`).then((r) => setDocs(r.data)).catch(() => setDocs([])); }, [qs]);
  useEffect(() => { load(); }, [load]);

  const del = async (d) => {
    if (!window.confirm(`Remove "${d.name}"?`)) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Document removed"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="documents-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><FileText className="w-4 h-4" /> Documents & agreements</h3>
        {canManage && <button onClick={() => setOpen(true)} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="panel-file-document"><Plus className="w-4 h-4" /> File</button>}
      </div>
      {docs.length === 0 && <p className="text-sm text-slate-400">No documents filed yet.</p>}
      {docs.map((d) => (
        <div key={d.id} className={`group flex items-center gap-2 py-2 border-b last:border-0 ${expiringSoon(d) ? "border-l-2 border-l-amber-400 bg-amber-50 rounded-md px-2" : ""}`} style={{ borderColor: "var(--line)" }} data-testid={`panel-doc-${d.id}`}>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-700 truncate">{d.name} <span className="text-slate-400">· {d.doc_type}</span></div>
            <div className="text-[11px] text-slate-400">{d.counterparty_name || "No counterparty"}{d.expiry_date ? ` · expires ${d.expiry_date}` : ""}</div>
          </div>
          <StatusChip status={d.status} />
          <button onClick={() => downloadDoc(d)} className="p-1 text-slate-400 hover:text-slate-800" title="Open" data-testid={`panel-doc-open-${d.id}`}>{d.kind === "link" ? <ExternalLink className="w-4 h-4" /> : <Download className="w-4 h-4" />}</button>
          {canDelete(d) && <button onClick={() => del(d)} className="p-1 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100" title="Remove" data-testid={`panel-doc-del-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
        </div>
      ))}
      <FileDocumentDialog open={open} onOpenChange={setOpen} onDone={load} fixedOrgId={organizationId} fixedOppId={opportunityId} fixedCounterpartyName={orgName} />
    </div>
  );
}
